import { SITE_DYNAMIC_PREFIXES, SITE_STATIC_PATHS, } from '@zuruflex/types';
import { contentNodeText } from './content-doc.js';
/**
 * What to look at before a page goes live.
 *
 * Advice, never a gate: an owner publishing an emergency correction must
 * not be argued with by a checklist. It catches the mistakes that survive
 * a read-through — a "[to be confirmed]" nobody came back to, a picture a
 * screen reader will announce as nothing, a link to a page that is not there.
 */
/** A bracketed note left for later: "[to be confirmed]", "[company number]". */
const PLACEHOLDER = /\[[^\]\n]{2,60}\]/g;
export function reviewContent(doc, 
/** Paths of the managed pages that are live, from `contentPath`. */
livePaths) {
    const live = new Set(livePaths);
    const found = new Map();
    const add = (code, detail) => {
        const key = `${code}:${detail ?? ''}`;
        const existing = found.get(key);
        if (existing)
            existing.count += 1;
        else
            found.set(key, { code, ...(detail ? { detail } : {}), count: 1 });
    };
    const isKnown = (href) => {
        const path = href.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
        return (live.has(path) ||
            SITE_STATIC_PATHS.includes(path) ||
            SITE_DYNAMIC_PREFIXES.some((prefix) => path.startsWith(prefix)));
    };
    let lastLevel = 1;
    const walk = (node) => {
        if (node.type === 'image' && !node.attrs?.alt?.trim())
            add('IMAGE_NO_ALT');
        if (node.type === 'heading') {
            const level = node.attrs?.level ?? 2;
            if (!contentNodeText(node).trim())
                add('EMPTY_HEADING');
            // H2 straight to H4 breaks the outline a screen reader navigates by.
            if (level - lastLevel > 1 && lastLevel !== 1)
                add('HEADING_SKIP', `H${lastLevel} → H${level}`);
            lastLevel = level;
        }
        if (node.type === 'text' && node.text) {
            for (const match of node.text.matchAll(PLACEHOLDER))
                add('PLACEHOLDER', match[0]);
            const href = node.marks?.find((mark) => mark.type === 'link')?.attrs?.href;
            if (href?.startsWith('/') && !isKnown(href))
                add('UNKNOWN_LINK', href);
        }
        node.content?.forEach(walk);
    };
    (doc.content ?? []).forEach(walk);
    return [...found.values()];
}
